/**
 * Accessors for {@link BuildingGraphFaceNode.parentHandle}.
 *
 * Face nodes are either a face of a volume node BRep (`VOLUME`) or authored directly
 * from a doc entity that has no volume, such as a freeform wall path segment
 * (`FREEFORM_WALL`). Callers should read geometry through {@link faceNodeGetFace}
 * rather than reaching into volume BReps, so both provenances behave the same.
 */
import type {
  BuildingGraph,
  BuildingGraphFaceNode,
  BuildingGraphFaceNodeParentHandle,
  BuildingGraphVolumeNode,
} from "building-types/building/buildingGraphTypes";
import type { Table } from "common/data/table";
import type { Face } from "geom/brep";

export type BuildingGraphFaceNodeVolumeParentHandle = Extract<BuildingGraphFaceNodeParentHandle, { type: "VOLUME" }>;
export type BuildingGraphFaceNodeFreeformWallParentHandle = Extract<
  BuildingGraphFaceNodeParentHandle,
  { type: "FREEFORM_WALL" }
>;

/** The `VOLUME` parent handle of a face node, or undefined for non-volume face nodes. */
export function faceNodeVolumeParentHandle(
  faceNode: BuildingGraphFaceNode | undefined,
): BuildingGraphFaceNodeVolumeParentHandle | undefined {
  if (faceNode?.parentHandle.type !== "VOLUME") {
    return undefined;
  }
  return faceNode.parentHandle;
}

/** The `FREEFORM_WALL` parent handle of a face node, or undefined for other face nodes. */
export function faceNodeFreeformWallParentHandle(
  faceNode: BuildingGraphFaceNode | undefined,
): BuildingGraphFaceNodeFreeformWallParentHandle | undefined {
  if (faceNode?.parentHandle.type !== "FREEFORM_WALL") {
    return undefined;
  }
  return faceNode.parentHandle;
}

export function faceNodeGetVolumeNodeId(faceNode: BuildingGraphFaceNode | undefined): string | undefined {
  return faceNodeVolumeParentHandle(faceNode)?.volumeNodeId;
}

export function faceNodeGetVolumeNodeFaceIndex(faceNode: BuildingGraphFaceNode | undefined): number | undefined {
  return faceNodeVolumeParentHandle(faceNode)?.volumeNodeFaceIndex;
}

export function faceNodeGetVolumeNodeFromTable(
  faceNode: BuildingGraphFaceNode | undefined,
  volumeNodes: Table<BuildingGraphVolumeNode>,
): BuildingGraphVolumeNode | undefined {
  const volumeParentHandle = faceNodeVolumeParentHandle(faceNode);
  if (!volumeParentHandle) {
    return undefined;
  }
  return volumeNodes[volumeParentHandle.volumeNodeId];
}

/** The parent volume node of a face node, or undefined when the face node has no volume parent. */
export function faceNodeGetVolumeNode(
  faceNode: BuildingGraphFaceNode | undefined,
  buildingGraph: Pick<BuildingGraph, "volumeNodes">,
): BuildingGraphVolumeNode | undefined {
  return faceNodeGetVolumeNodeFromTable(faceNode, buildingGraph.volumeNodes);
}

export function faceNodeGetFaceFromTable(
  faceNode: BuildingGraphFaceNode | undefined,
  volumeNodes: Table<BuildingGraphVolumeNode>,
): Face | undefined {
  if (!faceNode) {
    return undefined;
  }
  const volumeParentHandle = faceNodeVolumeParentHandle(faceNode);
  if (!volumeParentHandle) {
    return faceNode.face;
  }
  const volumeNode = volumeNodes[volumeParentHandle.volumeNodeId];
  return volumeNode?.brep?.faces[volumeParentHandle.volumeNodeFaceIndex];
}

/** Geometry of a face node, resolved from the parent volume BRep or from the face node itself. */
export function faceNodeGetFace(
  faceNode: BuildingGraphFaceNode | undefined,
  buildingGraph: Pick<BuildingGraph, "volumeNodes">,
): Face | undefined {
  return faceNodeGetFaceFromTable(faceNode, buildingGraph.volumeNodes);
}
